import { useState } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dagltn3ep053ijub2d6g';

interface FormState {
  status: 'idle' | 'success' | 'error';
  message: string;
}

export default function ContactSection() {
  const [state, setState] = useState<FormState>({ status: 'idle', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // Anti-spam honeypot
    const honeypot = (fd.get('company_alt') as string | null)?.trim() ?? '';
    if (honeypot) {
      setState({ status: 'success', message: '提交成功，我们会尽快与你联系！' });
      form.reset();
      return;
    }

    // remove empty honeypot field
    fd.delete('company_alt');

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (typeof value === 'string' && value.trim()) {
        payload.append(key, value);
      }
    });

    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? parsed) as Record<string, string> | undefined;
      const code = parsed?.code as string | undefined;
      const serverMsg =
        meta?.message || meta?.detail || (parsed?.message as string | undefined) || responseText || '';

      const isSpamServer = /spam/i.test(serverMsg) || serverMsg.includes('form data is spam');
      if (!res.ok || code !== 'OK' || isSpamServer) {
        setState({
          status: 'error',
          message: serverMsg || '提交失败，请稍后重试。',
        });
        return;
      }
      setState({ status: 'success', message: '提交成功，我们会尽快与你联系！' });
      form.reset();
    } catch {
      setState({ status: 'error', message: '网络异常，请稍后重试。' });
    }
  };

  return (
    <section id="tougao" className="w-full scroll-mt-20 bg-background-100/70">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-2 lg:gap-16">
        <div>
          <p className="mb-2 text-sm font-medium text-primary-600">想一起围观，也想被围观</p>
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">投稿你身边的“瓜”</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            无论是朝阳街头巷尾的新鲜事，还是你正追着的朝阳吃瓜热门话题，都可以发给我们。
            编辑部核实来源后会择优呈现在对应频道，让你的热议焦点被更多人看到。
          </p>

          <ul className="mt-8 space-y-5 text-sm">
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700"><i className="ri-mail-line"></i></span>
              <div>
                <p className="font-medium text-foreground-900">合作与媒体联系</p>
                <p className="text-foreground-600">hi@zhaoyangchigua.cn</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-700"><i className="ri-time-line"></i></span>
              <div>
                <p className="font-medium text-foreground-900">审稿与回复时间</p>
                <p className="text-foreground-600">工作日 09:00 – 21:00（法定节假日顺延）</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-800"><i className="ri-send-plane-line"></i></span>
              <div>
                <p className="font-medium text-foreground-900">投稿须知</p>
                <p className="text-foreground-600">请附上事件发生地、时间与可核实线索，避免使用未经证实的信息。</p>
              </div>
            </li>
          </ul>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form="朝阳吃瓜-话题投稿与意见反馈"
          className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-sm md:p-7"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-sm font-medium text-foreground-800">怎么称呼你</label>
              <input
                id="name"
                name="name"
                required
                maxLength={30}
                placeholder="你的昵称"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium text-foreground-800">联系邮箱</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="name@example.com"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-1.5">
            <label htmlFor="topic" className="text-sm font-medium text-foreground-800">所属栏目</label>
            <select
              id="topic"
              name="topic"
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-400"
            >
              <option value="明星八卦">明星八卦</option>
              <option value="综艺现场">综艺现场</option>
              <option value="影视娱乐">影视娱乐</option>
              <option value="体育竞技">体育竞技</option>
              <option value="网红电商">网红电商</option>
              <option value="本地民生">本地民生</option>
              <option value="其他/建议反馈">其他 / 建议反馈</option>
            </select>
          </div>

          <div className="mt-4 flex flex-col gap-1.5">
            <label htmlFor="content" className="text-sm font-medium text-foreground-800">详细内容</label>
            <textarea
              id="content"
              name="content"
              required
              rows={5}
              maxLength={500}
              placeholder="请尽量描述事件经过、发生地、时间以及线索来源……"
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            ></textarea>
            <p className="text-right text-[11px] text-foreground-400">最多 500 字</p>
          </div>

          <input
            type="text"
            name="company_alt"
            className="hp-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            value=""
          />

          {state.status === 'error' && (
            <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 p-3 text-sm text-primary-800">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{state.message}</span>
            </p>
          )}
          {state.status === 'success' && (
            <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 p-3 text-sm text-accent-800">
              <i className="ri-checkbox-circle-line mt-0.5"></i>
              <span>{state.message}</span>
            </p>
          )}

          <button
            type="submit"
            className="mt-5 w-full whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
          >
            提交投稿 / 反馈
          </button>
        </form>
      </div>
    </section>
  );
}